<template>
  <div class="inbox-area">
    <div class="l-block__header">
      <TitleSub level="h2">
        <Icon2 size="18" variant="search" />
        <span class="title">調査したいキーワード</span>
      </TitleSub>
    </div>
    <div class="c-alertPos">
      <Input
        v-model="params.keyword"
        placeholder="（例）SEO対策"
        :disabled="isProcessing || isRequiredResurvey"
        :class="{
          alert: isEdited.keyword && error.keyword,
          warning: isRequiredResurvey
        }"
        v-focus="isTopPage"
        @focusout="focusoutKeyword"
      />
      <Alert v-if="isEdited.keyword && error.keyword">
        {{ error.keyword }}
      </Alert>
      <Alert v-else-if="errorResponse">
        {{ errorResponse }}
      </Alert>
      <Alert v-else-if="isRequiredSurveyResurvey" variant="warning">
        記事作成マニュアルをご利用になる場合は再調査をお願いします。
      </Alert>
      <Alert v-else-if="isRequiredWritingResurvey" variant="warning">
        タイトル・見出し設計をご利用になる場合は再調査をお願いします。
      </Alert>
    </div>
    <Button
      size="w230"
      fsize="f16"
      variant="main"
      :disabled="isDisabledButton"
      :class="{ 'is-loading': isProcessing }"
      @click="registerKeyword"
    >
      {{ isRequiredResurvey ? '再調査する' : '調査する' }}
    </Button>
  </div>
</template>

<script>
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Icon2 from '@/components/Icon2';
import Input from '@/components/Input';
import TitleSub from '@/components/TitleSub';
import Validation from '@/validation';
import Amplitude from '@/amplitude';
import apiProjects from '@/api/projects';
import utils from '@/utils';

export default {
  props: {
    createdAt: {
      type: String,
      required: false
    },
    keyword: {
      type: String,
      required: false
    },
    project: {
      type: Object,
      required: true
    }
  },

  components: {
    Alert,
    Button,
    Input,
    Icon2,
    TitleSub
  },

  data() {
    return {
      error: {
        keyword: ''
      },
      errorResponse: '',
      params: {
        keyword: this.keyword || ''
      },
      isEdited: {
        keyword: false
      },
      isError: false,
      isProcessing: false
    };
  },

  computed: {
    toName() {
      let name = this.$route.name;
      if (this.isTopPage || this.isWritingPage) {
        name = 'contentPlansSurvey';
      }
      return name;
    },
    isAfterManualReleaseDate() {
      // dateJst日付形式で分析日が送信されているので、同じ形式に変換
      const releaseDate = this.$options.filters.dateText(
        process.env.VUE_APP_CONTENT_PLAN_ADD_MANUAL_DATE,
        'YYYY-MM-DD'
      );
      // 日付レベル比較になってしまうので、リリースの翌日以降しか判定Trueを返さないようにする
      return utils.isBeforeDate(releaseDate, this.createdAt);
    },
    isDisabledButton() {
      return (
        this.isError ||
        this.isProcessing ||
        // 再調査ではない時は、キーワードを変更しないとボタンをクリックできないようにする
        (!this.isRequiredSurveyResurvey &&
          !this.isRequiredWritingResurvey &&
          !this.isEditedKeyword &&
          this.$route.name !== 'contentPlans')
      );
    },
    isEditedKeyword() {
      return this.params.keyword !== this.keyword;
    },
    isEnableAiNewContent() {
      // dateJst日付形式で分析日が送信されているので、同じ形式に変換
      const date = this.$options.filters.dateText(
        process.env.VUE_APP_CONTENT_PLAN_ADD_AI_NEW_CONTENT_DATE,
        'YYYY-MM-DD'
      );
      return utils.isSameOrAfterDate(this.createdAt, date);
    },
    isRequiredResurvey() {
      return this.isRequiredSurveyResurvey || this.isRequiredWritingResurvey;
    },
    isRequiredSurveyResurvey() {
      return this.isSurveyPage && !this.isAfterManualReleaseDate;
    },
    isRequiredWritingResurvey() {
      return this.isWritingPage && !this.isEnableAiNewContent;
    },
    isTopPage() {
      return this.$route.name === 'contentPlans';
    },
    isSurveyPage() {
      return this.$route.name === 'contentPlansSurvey';
    },
    isWritingPage() {
      return this.$route.name === 'contentPlansWriting';
    }
  },

  watch: {
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    params: {
      handler() {
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponse = '';
      },
      deep: true
    },
    'params.keyword': {
      handler(value, oldValue) {
        const keyword = this.$options.filters.replaceDoubleSpaceToSpace(
          this.params.keyword.trim()
        );
        this.error.keyword = new Validation()
          .input()
          .maxLength(255)
          .test(keyword);
        this.isEdited.keyword = keyword ? true : false;
      },
      immediate: true
    }
  },

  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },
    focusoutKeyword() {
      this.params.keyword = this.$options.filters.replaceDoubleSpaceToSpace(
        this.params.keyword
      );
    },
    getUsages() {
      const params = {
        items: 'content-plan-keywords'
      };
      return apiProjects.usages.get(params, this.auth.idToken, this.project.id);
    },
    registerKeyword() {
      if (this.isError || this.isProcessing) {
        return;
      }
      this.isProcessing = true;

      this.getUsages()
        .then(response => {
          let current = response.data.items[0].current;
          let max = response.data.items[0].max;
          if (current >= max) {
            this.errorResponse = `月間に登録できる上限数を超えています`;
            this.isProcessing = false;
            return;
          }
          const params = {
            keyword: this.params.keyword
          };
          apiProjects.contentPlans.keyword
            .post(params, this.auth.idToken, this.project.id)
            .then(response => {
              Amplitude.postLogEvent('RegisterContentPlanKeyword');
              this.goSurvey(this.params.keyword);
            })
            .catch(error => {
              if (error.response && error.response.data) {
                this.errorResponse = error.response.data.errors[0].userMessage;
              }
              this.isProcessing = false;
            });
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          }
          this.isProcessing = false;
        });
    },
    goSurvey(keyword) {
      const date = this.$options.filters.dateText(
        utils.getDateResetHours(),
        'YYYY-MM-DD'
      );
      this.$router.push({
        name: this.toName,
        query: { createdAt: date, keyword: keyword }
      });
    }
  }
};
</script>
